import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight, Sparkles } from "lucide-react";
import { scrollToHash } from "@/components/Navbar";
import HeroCanvas from "@/components/HeroCanvas";
import { useLanguage } from "@/lib/i18n";

const lines = [
  { key: "hero.line1", accent: false },
  { key: "hero.line2", accent: false },
  { key: "hero.line3", accent: true },
];

const stats = [
  { value: "3", key: "hero.stat1" },
  { value: "1", key: "hero.stat2" },
  { value: "24/7", key: "hero.stat3" },
];

function Magnetic({ children }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 14, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 180, damping: 14, mass: 0.4 });
  return (
    <motion.div
      ref={ref}
      style={{ x: sx, y: sy }}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        x.set((e.clientX - r.left - r.width / 2) * 0.28);
        y.set((e.clientY - r.top - r.height / 2) * 0.28);
      }}
      onMouseLeave={() => {
        x.set(0);
        y.set(0);
      }}
      className="inline-block"
    >
      {children}
    </motion.div>
  );
}

export default function Hero({ active }) {
  const ref = useRef(null);
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const anim = (val) => (active ? val : {});

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-[72px]" data-testid="hero-section">
      <HeroCanvas />
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#FF5A00]/10 blur-[160px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#FFB800]/10 blur-[160px]" />
      </div>

      <motion.div style={{ y: textY, opacity: fade }} className="max-w-[1400px] mx-auto px-6 md:px-10 w-full py-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={anim({ opacity: 1, y: 0 })}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="flex items-center gap-2 mb-8"
          data-testid="hero-badge"
        >
          <Sparkles className="w-4 h-4 text-[#FFB800]" />
          <span className="text-xs md:text-sm tracking-[0.3em] uppercase text-neutral-400">
            {t("hero.badge")}
          </span>
        </motion.div>

        <h1 className="font-syne font-bold text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tighter max-w-5xl" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={active ? { y: "0%" } : { y: "110%" }}
                transition={{ duration: 1, delay: 0.35 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${line.accent ? "text-[#FF5A00]" : ""}`}
              >
                {t(line.key)}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={anim({ opacity: 1, y: 0 })}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-neutral-400"
          data-testid="hero-subtext"
        >
          {t("hero.subtext")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={anim({ opacity: 1, y: 0 })}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Magnetic>
            <a
              href="#contact"
              onClick={scrollToHash("#contact")}
              className="group flex items-center gap-2 bg-[#FF5A00] text-black font-medium px-8 py-4 rounded-full hover:bg-[#FFB800] transition-colors duration-300"
              data-testid="hero-cta-start"
            >
              {t("hero.ctaStart")}
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Magnetic>
          <Magnetic>
            <a
              href="#services"
              onClick={scrollToHash("#services")}
              className="group flex items-center gap-2 border border-white/20 text-neutral-100 px-8 py-4 rounded-full hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors duration-300"
              data-testid="hero-cta-explore"
            >
              {t("hero.ctaExplore")}
              <ArrowDown className="w-5 h-5 transition-transform duration-300 group-hover:translate-y-1" />
            </a>
          </Magnetic>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={anim({ opacity: 1 })}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 grid grid-cols-3 max-w-lg border-t border-white/10 pt-8 gap-6"
          data-testid="hero-stats"
        >
          {stats.map((s) => (
            <div key={s.key}>
              <p className="font-syne text-3xl md:text-4xl font-bold text-[#FFB800]">{s.value}</p>
              <p className="text-xs md:text-sm text-neutral-500 mt-1">{t(s.key)}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={anim({ opacity: 1 })}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-neutral-600"
        data-testid="hero-scroll-hint"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">{t("hero.scroll")}</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[#FF5A00] to-transparent"
        />
      </motion.div>
    </section>
  );
}
